import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { FormInput } from "../../../components/common/FormInput";
import { SimpleDropdown } from "../../../components/common/SimpleDropdown";
import adminService from "../../../services/adminService";

// Manual Edits > Team Profiles: search every team (hidden ones too) on the
// left, edit any field of the selected team's profile on the right.

const TEAMS_PER_PAGE = 20;
const MAX_LOGO_MB = 5;

// Database division codes and what the site calls them (backend divisionMap.js)
const DIVISION_LABELS = { I: "NJCAA D1", II: "NJCAA D2", III: "NJCAA D3", IV: "NorCal", V: "SoCal" };
const divisionLabel = (value) => (DIVISION_LABELS[value] ? `${DIVISION_LABELS[value]} (${value})` : value);

const DETAIL_FIELDS = [
  { key: "location", label: "Location", example: "e.g. Panama City, FL" },
  { key: "conference", label: "Conference", example: "e.g. Panhandle" },
  { key: "region", label: "Region", example: "e.g. Region 8" },
  { key: "coachName", label: "Head Coach", example: "e.g. John Smith" },
];
const RECORD_FIELDS = [
  { key: "overallRecord", label: "Overall Record", example: "e.g. 32-18" },
  { key: "home", label: "Home Record", example: "e.g. 18-7" },
  { key: "away", label: "Away Record", example: "e.g. 12-9" },
  { key: "neutral", label: "Neutral Record", example: "e.g. 2-2" },
];
const TEXT_KEYS = ["name", "division", ...DETAIL_FIELDS.map((f) => f.key), ...RECORD_FIELDS.map((f) => f.key)];

const toForm = (team) => ({
  ...Object.fromEntries(TEXT_KEYS.map((key) => [key, team?.[key] || ""])),
  rank: team?.rank === "" || team?.rank == null ? "" : String(team.rank),
  isActive: team?.isActive !== false,
  logoUrl: team?.logo || "",
  topPerformerPlayerId: team?.topPerformer?.playerId ? String(team.topPerformer.playerId) : "",
  topPerformerStats: team?.topPerformer?.stats || "",
});

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";

const SectionLabel = ({ children }) => (
  <p className="md:col-span-2 text-jp-accent-ink text-[11px] font-bold uppercase tracking-widest pt-2 border-t border-jp-line">{children}</p>
);

const TeamLogo = ({ src, className }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <div className={`${className} bg-jp-section border border-jp-line`} />;
  return <img src={src} alt="" onError={() => setFailed(true)} className={`${className} object-contain bg-white border border-jp-line`} />;
};

const TeamEditForm = ({ team, divisions, onSaved }) => {
  const [form, setForm] = useState(() => toForm(team));
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rosterError, setRosterError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => () => logoPreview && URL.revokeObjectURL(logoPreview), [logoPreview]);

  // Roster for the top performer picker
  useEffect(() => {
    let cancelled = false;
    adminService
      .getTeamRosterForEdit(team.id)
      .then((data) => !cancelled && setRoster(data.players || []))
      .catch(() => !cancelled && setRosterError("Could not load the roster."));
    return () => {
      cancelled = true;
    };
  }, [team.id]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
    setSuccess("");
  };

  const handleLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return setError("The logo must be an image (JPG, PNG, GIF or WebP).");
    if (file.size > MAX_LOGO_MB * 1024 * 1024) return setError(`The logo must be under ${MAX_LOGO_MB} MB.`);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setError("");
    setSuccess("");
  };

  const resetLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("The team name can't be empty.");
    if (form.rank.trim() && !/^\d+$/.test(form.rank.trim())) return setError("Rank must be a whole number, or empty.");

    const original = toForm(team);
    const formData = new FormData();
    TEXT_KEYS.forEach((key) => formData.append(key, form[key].trim()));
    formData.append("rank", form.rank.trim());
    formData.append("isActive", String(form.isActive));
    if (logoFile) formData.append("teamLogo", logoFile);
    else formData.append("logoUrl", form.logoUrl.trim());
    // Only send the top performer when it changed, so a player who has since
    // left the team doesn't block saving other fields
    if (form.topPerformerPlayerId !== original.topPerformerPlayerId || form.topPerformerStats !== original.topPerformerStats) {
      formData.append("topPerformerPlayerId", form.topPerformerPlayerId);
      formData.append("topPerformerStats", form.topPerformerStats.trim());
    }

    setSaving(true);
    try {
      const data = await adminService.updateTeam(team.id, formData);
      resetLogo();
      setForm(toForm(data.team));
      setSuccess("Team saved.");
      onSaved(data.team);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the team. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Keep the current value selectable even if it isn't in the list
  const divisionOptions = [...new Set([...divisions, form.division].filter(Boolean))].map((value) => ({
    value,
    label: divisionLabel(value),
  }));
  const performerOptions = [
    { value: "", label: "None" },
    ...roster.map((p) => ({ value: String(p.id), label: p.position ? `${p.name} (${p.position})` : p.name })),
  ];
  if (form.topPerformerPlayerId && !roster.some((p) => String(p.id) === form.topPerformerPlayerId)) {
    performerOptions.push({
      value: form.topPerformerPlayerId,
      label: `${team.topPerformer?.name || "Current player"} (not on this roster)`,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="bg-jp-card border border-jp-line rounded-2xl p-4 flex flex-col gap-4 max-h-[700px] overflow-y-auto text-left">
      {/* Header */}
      <div className="flex items-center gap-4">
        <TeamLogo src={logoPreview || form.logoUrl} className="w-16 h-16 rounded-full p-1.5 flex-shrink-0" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-jp-ink truncate">{team.name}</h3>
          {!team.isActive && <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">Hidden from the site</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        {/* Basics */}
        <div className="md:col-span-2">
          <FormInput label="Team Name" name="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Gulf Coast State College" />
        </div>
        <div>
          <label className="block text-left text-jp-ink text-sm font-roboto mb-3">Division</label>
          <SimpleDropdown variant="form" value={form.division} onChange={(value) => update("division", value)} options={divisionOptions} placeholder="Select..." />
        </div>
        <FormInput label="Rank" name="rank" type="number" value={form.rank} onChange={(e) => update("rank", e.target.value)} placeholder="Leave empty for no rank" />
        {DETAIL_FIELDS.map((field) => (
          <FormInput key={field.key} label={field.label} name={field.key} value={form[field.key]} onChange={(e) => update(field.key, e.target.value)} placeholder={field.example} />
        ))}

        {/* Logo */}
        <SectionLabel>Logo</SectionLabel>
        <div className="md:col-span-2 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 rounded-full border border-jp-line bg-jp-section hover:bg-jp-line text-jp-ink text-xs font-medium transition-colors"
          >
            Upload an image
          </button>
          {logoPreview && (
            <>
              <span className="text-[11px] text-jp-muted">New logo is saved when you click Save.</span>
              <button type="button" onClick={resetLogo} className="text-xs text-jp-muted hover:text-jp-ink underline">
                Undo
              </button>
            </>
          )}
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={(e) => handleLogo(e.target.files?.[0])} />
        </div>
        {!logoPreview && (
          <div className="md:col-span-2">
            <FormInput
              label="Or paste a link to the logo"
              name="logoUrl"
              value={form.logoUrl}
              onChange={(e) => update("logoUrl", e.target.value)}
              placeholder="https://..."
              helperText="(empty = no logo)"
            />
          </div>
        )}

        {/* Records */}
        <SectionLabel>Records</SectionLabel>
        {RECORD_FIELDS.map((field) => (
          <FormInput key={field.key} label={field.label} name={field.key} value={form[field.key]} onChange={(e) => update(field.key, e.target.value)} placeholder={field.example} />
        ))}

        {/* Top performer */}
        <SectionLabel>Top Performer</SectionLabel>
        <div>
          <label className="block text-left text-jp-ink text-sm font-roboto mb-3">Player</label>
          <SimpleDropdown
            variant="form"
            value={form.topPerformerPlayerId}
            onChange={(value) => update("topPerformerPlayerId", value)}
            options={performerOptions}
            placeholder="None"
          />
          {rosterError && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{rosterError}</p>}
        </div>
        <FormInput label="Stats" name="topPerformerStats" value={form.topPerformerStats} onChange={(e) => update("topPerformerStats", e.target.value)} placeholder="e.g. .412 AVG, 9 HR, 41 RBI" />

        {/* Visibility */}
        <SectionLabel>Visibility</SectionLabel>
        <label className="md:col-span-2 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => update("isActive", e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-cyan-500"
          />
          <span>
            <span className="block text-sm text-jp-ink">Show this team on the site</span>
            <span className="block text-[11px] text-jp-muted">
              Hidden teams don't appear anywhere on the site: team pages, searches, filters or rosters.
            </span>
          </span>
        </label>
        {!form.isActive && team.isActive && (
          <p className="md:col-span-2 text-xs text-amber-700 dark:text-amber-300">
            Saving will hide {team.name} from the whole site.
          </p>
        )}
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {success && <p className="text-sm text-green-600 dark:text-green-400">{success}</p>}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="min-w-[160px] h-10 px-5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
};

export const ManualEditsTeams = () => {
  const [teams, setTeams] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const requestRef = useRef(0);

  const loadTeams = useCallback(async (page, searchText, append) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await adminService.getTeamsForEdit({ page, limit: TEAMS_PER_PAGE, search: searchText || undefined });
      if (requestId !== requestRef.current) return; // a newer search replaced this one
      setTeams((prev) => (append ? [...prev, ...data.teams] : data.teams));
      setPagination(data.pagination);
      setDivisions(data.divisions || []);
    } catch (err) {
      if (requestId === requestRef.current) setError(err?.response?.data?.message || "Could not load teams.");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  // Search as you type (debounced)
  useEffect(() => {
    const timer = setTimeout(() => loadTeams(1, search.trim(), false), 400);
    return () => clearTimeout(timer);
  }, [search, loadTeams]);

  const handleSaved = (saved) => {
    setTeams((prev) => prev.map((team) => (team.id === saved.id ? saved : team)));
    setSelectedTeam(saved);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1.4fr,1.4fr] gap-6">
      {/* Team list */}
      <div className="flex flex-col h-full">
        <div className="relative w-full md:max-w-md mb-3">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-jp-muted">
            <FiSearch size={16} />
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by team, location, conference, region or coach..."
            className="w-full h-9 pl-9 pr-8 rounded-lg bg-jp-card border border-jp-line text-xs text-jp-ink placeholder:text-jp-muted focus:outline-none"
          />
        </div>

        <div className="rounded-2xl bg-jp-card border border-jp-line overflow-hidden flex-1 flex flex-col max-h-[700px]">
          <div className="overflow-y-auto flex-1">
            <table className="min-w-full text-[11px] border-collapse">
              <thead className="bg-jp-card text-jp-ink sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left bg-jp-card">TEAM</th>
                  <th className="px-4 py-2 text-center bg-jp-card">DIVISION</th>
                  <th className="px-4 py-2 text-center bg-jp-card">CONFERENCE</th>
                  <th className="px-4 py-2 text-center bg-jp-card">LAST UPDATE</th>
                  <th className="px-4 py-2 text-center bg-jp-card">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-red-600 dark:text-red-400">{error}</td>
                  </tr>
                ) : loading && teams.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-jp-muted">Loading...</td>
                  </tr>
                ) : teams.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-jp-muted">
                      {search ? "No matching teams found" : "No teams found"}
                    </td>
                  </tr>
                ) : (
                  teams.map((team) => (
                    <tr
                      key={team.id}
                      onClick={() => setSelectedTeam(team)}
                      className={`cursor-pointer border-t border-jp-line hover:bg-jp-section ${selectedTeam?.id === team.id ? "bg-jp-accent/10" : ""}`}
                    >
                      <td className="px-4 py-2 text-jp-ink">
                        <div className="flex items-center gap-2">
                          <TeamLogo src={team.logo} className="w-6 h-6 rounded-full p-0.5 flex-shrink-0" />
                          <span>{team.name}</span>
                          {!team.isActive && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[9px] font-bold uppercase">Hidden</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-jp-ink text-center">{team.division ? divisionLabel(team.division) : "-"}</td>
                      <td className="px-4 py-2 text-jp-ink text-center">{team.conference || "-"}</td>
                      <td className="px-4 py-2 text-jp-ink text-center">{formatDate(team.updatedAt)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className="text-jp-ink text-[11px] hover:underline">Edit ✏️</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination?.hasMore && (
            <div className="px-6 py-4 border-t border-jp-line flex justify-center">
              <button
                onClick={() => loadTeams(pagination.currentPage + 1, search.trim(), true)}
                disabled={loading}
                className="px-6 py-2 rounded-lg bg-sky-600 text-white text-sm font-medium hover:bg-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}

          {pagination && (
            <div className="px-6 py-3 border-t border-jp-line text-center">
              <p className="text-xs text-jp-muted">
                Showing {teams.length} of {pagination.totalCount} teams
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Edit form */}
      {selectedTeam ? (
        <TeamEditForm key={selectedTeam.id} team={selectedTeam} divisions={divisions} onSaved={handleSaved} />
      ) : (
        <div className="bg-jp-card border border-jp-line rounded-2xl p-6 flex items-center justify-center text-sm text-jp-muted min-h-[200px]">
          Select a team to edit its profile.
        </div>
      )}
    </div>
  );
};

export default ManualEditsTeams;
